import { useQuery } from "@tanstack/react-query"
import type {
  UseBaseQueryOptions,
  UseQueryResult,
  QueryKey,
  WithRequired,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type { GetExportConfigurationsQueryResponse } from "../types/GetExportConfigurations"

type GetExportConfigurationsClient = typeof client<
  GetExportConfigurationsQueryResponse,
  Error,
  never
>
type GetExportConfigurations = {
  data: GetExportConfigurationsQueryResponse
  error: Error
  request: never
  pathParams: never
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<GetExportConfigurationsClient>>
  client: {
    parameters: Partial<Parameters<GetExportConfigurationsClient>[0]>
    return: Awaited<ReturnType<GetExportConfigurationsClient>>
  }
}
export const getExportConfigurationsQueryKey = () =>
  [{ url: "/v1/export/get-export-configurations" }] as const
export type GetExportConfigurationsQueryKey = ReturnType<
  typeof getExportConfigurationsQueryKey
>
export function getExportConfigurationsQueryOptions<
  TData = GetExportConfigurations["response"],
  TQueryData = GetExportConfigurations["response"],
>(
  options: GetExportConfigurations["client"]["parameters"] = {}
): WithRequired<
  UseBaseQueryOptions<
    GetExportConfigurations["response"],
    GetExportConfigurations["error"],
    TData,
    TQueryData
  >,
  "queryKey"
> {
  const queryKey = getExportConfigurationsQueryKey()
  return {
    queryKey,
    queryFn: async () => {
      const res = await client<
        GetExportConfigurations["data"],
        GetExportConfigurations["error"]
      >({
        method: "get",
        url: `/v1/export/get-export-configurations`,
        ...options,
      })
      return res
    },
  }
}
/**
 * @summary Get export configurations
 * @link /v1/export/get-export-configurations
 */
export function useGetExportConfigurations<
  TData = GetExportConfigurations["response"],
  TQueryData = GetExportConfigurations["response"],
  TQueryKey extends QueryKey = GetExportConfigurationsQueryKey,
>(
  options: {
    query?: Partial<
      UseBaseQueryOptions<
        GetExportConfigurations["response"],
        GetExportConfigurations["error"],
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: GetExportConfigurations["client"]["parameters"]
  } = {}
): UseQueryResult<TData, GetExportConfigurations["error"]> & {
  queryKey: TQueryKey
} {
  const { query: queryOptions, client: clientOptions = {} } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? getExportConfigurationsQueryKey()
  const query = useQuery<
    GetExportConfigurations["data"],
    GetExportConfigurations["error"],
    TData,
    any
  >({
    ...getExportConfigurationsQueryOptions<TData, TQueryData>(clientOptions),
    queryKey,
    ...queryOptions,
  }) as UseQueryResult<TData, GetExportConfigurations["error"]> & {
    queryKey: TQueryKey
  }
  query.queryKey = queryKey as TQueryKey
  return query
}
